import {
  Flex,
  Link,
  SkeletonText,
  SkeletonShape,
  FormattedRelativeTime,
  Text,
} from '@customerio/pluma-components/react';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import type { ServicesApiShippedResource } from '@/services/api/services-api';
import { isEmptyString } from '@/utils/string';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { useResourceInfo } from '../hooks';
import { resourceHref } from './recents-row';
import * as styles from './recently-launched.css';

const AVATAR_PALETTE = [
  '#C0392B',
  '#2471A3',
  '#1E8449',
  '#C87F0A',
  '#7D3C98',
  '#148F77',
  '#BA4A00',
  '#2980B9',
  '#1D8348',
  '#E74C3C',
  '#8E44AD',
  '#117A65',
  '#D35400',
  '#2C3E50',
  '#B7950B',
];

function getAvatarColors(name: string): { bg: string; fg: string } {
  let hash = 5381;
  for (let index_ = 0; index_ < name.length; index_++) {
    hash = ((hash << 5) + hash + name.charCodeAt(index_)) | 0;
  }
  const index = Math.abs(hash) % AVATAR_PALETTE.length;
  const hex = AVATAR_PALETTE[index];
  return { bg: `${hex}1A`, fg: hex };
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

const TYPE_FALLBACK_LABELS: Record<string, string> = {
  campaign: 'a campaign',
  newsletter: 'a broadcast',
  transactional: 'a transactional message',
  in_app_broadcast: 'an in-app message',
};

type ShippedResourceWithDisplayName = ServicesApiShippedResource & { displayName?: string };

interface ActivityItemProps {
  resource: ShippedResourceWithDisplayName;
  workspaceId: number;
}

function ActivityItem({ resource, workspaceId }: ActivityItemProps) {
  const { track } = useAnalytics();
  const { name, isNotFound } = useResourceInfo(workspaceId, resource.type, resource.id);
  const { user } = useAuth();
  const isCurrentUser = resource.user_id > 0 && resource.user_id === user.id;
  const displayName =
    resource.displayName ?? name ?? (isNotFound ? TYPE_FALLBACK_LABELS[resource.type] ?? resource.type : undefined);
  const avatarName = resource.user_id > 0 && !isEmptyString(resource.user_name) ? resource.user_name : 'System';
  const avatarColors = getAvatarColors(avatarName);

  return (
    <div className={styles.item}>
      <div
        aria-hidden="true"
        className={styles.initialsAvatar}
        style={assignInlineVars({
          [styles.avatarBg]: avatarColors.bg,
          [styles.avatarFg]: avatarColors.fg,
        })}
      >
        {getInitials(avatarName)}
      </div>
      <Flex direction="column" gap="025" minW="0" flexGrow="1" flexShrink="1">
        <Text as="div" text="product-ps" color="subtle">
          {isCurrentUser ? 'You' : avatarName} {resource.action}{' '}
          <Link
            href={resourceHref(workspaceId, resource)}
            variant="primary"
            onClick={() =>
              track('home', 'recentActivityClicked', { 'Resource Type': resource.type, Action: resource.action })
            }
          >
            {displayName ?? <SkeletonShape display="inline-block" style={{ width: 80, height: 14 }} />}
          </Link>
        </Text>
        <Text text="product-pxs" color="subtle">
          <FormattedRelativeTime date={resource.updated_at * 1000} />
        </Text>
      </Flex>
    </div>
  );
}

interface RecentlyLaunchedProps {
  shipped: ShippedResourceWithDisplayName[] | undefined;
  isLoading: boolean;
  workspaceId: number;
}

export function RecentlyLaunched({ shipped, isLoading, workspaceId }: RecentlyLaunchedProps) {
  if (isLoading) {
    return (
      <Flex direction="column" gap="050">
        {Array.from({ length: 3 }, (_, index) => (
          <div key={index} className={styles.item}>
            <SkeletonShape className={styles.skeletonCircle} />
            <Flex direction="column" gap="025" minW="0" flexGrow="1" flexShrink="1">
              <SkeletonText lines={2} />
            </Flex>
          </div>
        ))}
      </Flex>
    );
  }

  if (shipped == null || shipped.length === 0) {
    return null;
  }

  return (
    <Flex direction="column" gap="050">
      {shipped.map((resource) => (
        <ActivityItem key={`${resource.type}-${resource.id}`} resource={resource} workspaceId={workspaceId} />
      ))}
    </Flex>
  );
}
